import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { Base } from 'src/app/core/models/base.model';
import {
  Filter,
  QueryResult,
} from 'src/app/core/services/base/database.service';
import { DynamicService } from 'src/app/core/services/base/dynamic.service';
import { InputTextComponent } from 'src/app/shared/forms-v2/input-text/input-text.component';
import { AnchorChild, ColumnWithRegex } from '../../smart/table/table.component';

export enum TablesWithAutocompleteTemplate {
  CLIENTS = 'cpv_user_kyc_v2',
  AGENTS = 'cp_user',
  PII = 'cp_pii',
  CASE_CLIENTS = 'x_clients_overview_v1',
}

@Component({
  selector: 'app-user-search-autocomplete',
  templateUrl: './user-search-autocomplete.component.html',
  styleUrls: ['./user-search-autocomplete.component.scss'],
})
export class UserSearchAutocompleteComponent implements OnInit, OnChanges {
  @ViewChild('inputSearch') inputSearch!: InputTextComponent;

  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() searchString: string = '';
  @Input() readonly: boolean = false;
  @Input() columnsToSearch!: string[];
  @Input() columnsWithRegex?: ColumnWithRegex[] | null;
  @Input() coreFilters: Filter[] = [];
  @Input() tableName!: string;
  @Input() anchorChild?: AnchorChild;
  @Input() queryFunction: (search: string) => Observable<QueryResult<Base>> =
    this._performSearch;

  @Output() event = new EventEmitter<{
    emitter: string | null;
    context: { element: any; id: string };
  }>();
  @Output() onClearSelection = new EventEmitter<void>();
  @Output() onSearchChange = new EventEmitter<string>();

  _searchString = this.searchString;
  hasSelection = false;
  isLoading$ = new BehaviorSubject<boolean>(false);
  results: any[] | null = null;

  readonly TablesEnum = TablesWithAutocompleteTemplate;

  private _destroySubject = new Subject<void>();
  private searchChanged$ = new Subject<string>();
  private searchGeneration = 0;

  constructor(
    private router: Router,
    private sbs: DynamicService<Base>,
  ) { }

  ngOnInit(): void {
    if (!this.tableName) {
      throw new Error('tableName is required');
    }
    this.sbs.load(this.tableName);

    this.searchChanged$
      .pipe(
        debounceTime(400),
        takeUntil(this._destroySubject),
        switchMap((search) => {
          const generation = this.searchGeneration;
          if (!search?.trim()) {
            return of({ res: null, generation });
          }
          this.isLoading$.next(true);
          return this.queryFunction(search).pipe(
            catchError(() => of({ data: [], count: 0 })),
            map((res) => ({ res, generation })),
          );
        }),
      )
      .subscribe(({ res, generation }) => {
        this.isLoading$.next(false);
        if (generation !== this.searchGeneration) return;
        this.results = res ? (res as QueryResult<Base>).data : null;
      });

    this._updateAutoCompleteBasedOnInput();
  }

  /**
   * Updates the autocomplete based on the input string when the component is
   * initialized or when the input string changes.
   *
   * Nothing happens if the component is readonly.
   */
  private _updateAutoCompleteBasedOnInput() {
    if (!this.readonly && this.searchString?.length > 2) {
      this.searchChanged$.next(this.searchString);
      this.hasSelection = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchString'] && this.searchString !== this._searchString) {
      this._searchString = changes['searchString'].currentValue;
      this._updateAutoCompleteBasedOnInput();
    }
  }

  ngOnDestroy(): void {
    this.isLoading$.complete();
    this.searchChanged$.complete();
    this._destroySubject.next();
    this._destroySubject.complete();
  }

  onSearch(search: string) {
    if (!search?.trim()) {
      this.clearSelection();
    }
    this.searchChanged$.next(search);
  }

  private _performSearch(search: string) {
    const parts =
      (this.columnsToSearch ?? []).map(
        (c) => `${c}.ilike.%${search}%`
      );
    for (const item of this.columnsWithRegex ?? []) {
      const normalized = (search || '').replace(
        new RegExp(item.regexToBeRemoved, 'g'),
        ''
      );
      if (normalized.length > 0) {
        parts.push(`${item.columnName}.ilike.%${normalized}%`);
      }
    }
    const ilikeFilter: Filter = {
      column: '',
      operator: 'or',
      visible: true,
      value: parts.join(','),
    };

    return this.sbs.list(0, 20, [ilikeFilter, ...this.coreFilters]);
  }

  onAutocompleteSelect(element: any) {
    try {
      if (!this.anchorChild) return;

      const { id, prePath, emitEvent, newtab } = this.anchorChild;

      const idValue = id && element ? element[id] : undefined;
      const idToUse = idValue || (element && element.id) || undefined;
      if (!idToUse) return;

      if (emitEvent) {
        this.event.next({
          emitter: emitEvent,
          context: { element, id: idToUse },
        });
        this.hasSelection = true;
        return;
      }

      const preSegments = (prePath || '')
        .split('/')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      const currentUrl = this.router.url || window.location.pathname;
      const segments = (currentUrl || '')
        .split('/')
        .filter((s) => s && s.length > 0);
      const prefix = segments.length > 0 ? segments[0] : '';

      const routeArray = prefix
        ? [prefix, ...preSegments, idToUse]
        : [...preSegments, idToUse];

      if (newtab) {
        const url = this.router.serializeUrl(
          this.router.createUrlTree(routeArray)
        );
        window.open(url, '_blank');
      } else {
        this.router.navigate(routeArray).then((ok) => {
          console.log('router.navigate resolved', ok);
        });
      }
    } catch (err) {
      console.error('rowClick navigation error', err);
    }
  }

  clearSelection() {
    this.searchGeneration++;
    this.hasSelection = false;
    this.results = null;
    this._searchString = '';
    this.onClearSelection.emit();
  }

  onEnterPress() {
    this.hasSelection = true;
    this.onSearchChange.emit(this._searchString);

    if (this.inputSearch?.autocompleteTrigger?.panelOpen) {
      this.inputSearch.autocompleteTrigger.closePanel();
    }
  }
}
